import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectionStrategy, forwardRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subject, BehaviorSubject, of, timer } from 'rxjs';
import { debounce, distinctUntilChanged, switchMap, startWith, takeUntil, tap, catchError, map, shareReplay } from 'rxjs/operators';
import { OrganizationUnitItem } from '../../../features/user/models/filter-data.model';
import { OrganizationUnitService, OrganizationUnitDto } from '../../services/organization-unit.service';

/**
 * Define la fuente de datos del selector de unidades.
 *
 * - `'all'`  → búsqueda libre contra el backend (comportamiento original).
 * - `OrganizationUnitItem[]` → lista fija; el autocomplete filtra localmente sin llamadas HTTP.
 */
export type UnitSelectorSource = 'all' | OrganizationUnitItem[];
export type UnitSelectorGroupBy = 'auto' | 'level' | 'ancestors';
type UnitSelectorValue = string | string[] | null;
export type UnitSelectorSelection =
  | { kind: 'unit'; unit: OrganizationUnitItem }
  | { kind: 'text'; text: string }
  | { kind: 'query'; text: string };
interface FreeTextOption { kind: 'text'; text: string; }

const LEVEL_GROUP_ORDER_BY_ID = new Map<number, number>([
  [2, 1], // Area
  [3, 2], // Distrito
  [4, 3], // Grupo
  [5, 4], // Comunidad
]);

@Component({
  selector: 'app-unit-selector',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    MatProgressBarModule,
    MatTooltipModule
  ],
  templateUrl: './unit-selector.component.html',
  styleUrls: ['./unit-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UnitSelectorComponent),
      multi: true,
    }
  ]
})
export class UnitSelectorComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  /**
   * Fuente de datos para las opciones del autocomplete.
   * - `'all'`  → busca en el backend al escribir (comportamiento por defecto).
   * - `OrganizationUnitItem[]` → filtra localmente la lista provista.
   */
  @Input() unitSource: UnitSelectorSource = 'all';

  /** @deprecated Usar `unitSource` con un array. Se mantiene para compatibilidad. */
  @Input() set units(value: OrganizationUnitItem[]) {
    if (value && value.length > 0) {
      this._units = value;
    }
  }

  @Input() label = 'Unidad';
  @Input() placeholder?: string;
  @Input() floatLabel: 'auto' | 'always' = 'auto';
  @Input() required = true;
  /** Filtro opcional por nivel organizacional (solo aplica en modo `'all'`) */
  @Input() levelId?: number;
  /** Filtro opcional por varios niveles organizacionales (solo aplica en modo `'all'`) */
  @Input() allowedLevelIds: number[] = [];
  /** Tamaño de página para búsqueda backend (solo aplica en modo `'all'`) */
  @Input() pageSize = 20;
  /** Mínimo de caracteres para ejecutar búsqueda en backend (solo aplica en modo `'all'`) */
  @Input() minSearchLength = 2;
  /** Mostrar botón para limpiar la selección */
  @Input() showClearButton = true;
  /** Apariencia del mat-form-field; por defecto se usa fill. */
  @Input() appearance?: 'fill';
  /** Número máximo de opciones a mostrar en modo lista fija */
  @Input() maxDisplayOptions?: number;
  /** Agrupa las opciones por nivel organizacional (solo aplica en modo lista fija) */
  @Input() groupByLevel = false;
  /** Criterio de agrupacion cuando groupByLevel esta activo. */
  @Input() groupByMode: UnitSelectorGroupBy = 'auto';
  /** Permite representar null como "usar valor por defecto" en lugar de una seleccion vacia. */
  @Input() allowDefaultUnit = false;
  /** Nombre mostrado cuando el campo usa el valor por defecto. */
  @Input() defaultUnitName = 'unidad de pertenencia';
  /** Texto auxiliar mostrado cuando el campo usa el valor por defecto. */
  @Input() defaultHint = 'Se usara la unidad de pertenencia del miembro.';
  /** Texto del boton que activa la seleccion manual. */
  @Input() overrideActionLabel = 'Cambiar';
  /** Texto del boton que vuelve al valor por defecto. */
  @Input() defaultActionLabel = 'Usar unidad de pertenencia';
  /** Indica carga externa de la fuente fija de unidades. */
  @Input() loading = false;
  @Input() compactStyle = false;
  @Input() allowFreeText = false;
  @Input() unconfirmedFreeText = false;
  @Input() freeTextLabel = 'texto libre';
  @Input() hideLabel = false;
  @Input() multiple = false;
  @Input() suggestedUnits: OrganizationUnitItem[] = [];

  @Output() unitSelected = new EventEmitter<string>();
  @Output() unitChange = new EventEmitter<OrganizationUnitItem | null>();
  @Output() unitsChange = new EventEmitter<OrganizationUnitItem[]>();
  @Output() selectionChange = new EventEmitter<UnitSelectorSelection | null>();
  private confirmedFreeText: string | null = null;

  get freeTextCandidate(): string | null {
    const value = this.unitControl.value;
    if (!this.allowFreeText || typeof value !== 'string' || this.isGuid(value)) return null;
    const text = value.trim();
    if (this.multiple && this.selectedUnitIds.some(selected => selected.toLowerCase() === text.toLowerCase())) return null;
    return text && (this.unconfirmedFreeText || text !== this.confirmedFreeText) ? text : null;
  }

  selectedUnitIds: string[] = [];
  private readonly selectedUnitCache = new Map<string, OrganizationUnitItem>();

  get selectedUnits(): OrganizationUnitItem[] {
    return this.selectedUnitIds.map(id => this.selectedUnitCache.get(id.toLowerCase()) ?? {
      id, name: this.allowFreeText && !this.isGuid(id) ? id : 'Unidad seleccionada', levelId: 0, levelName: '',
    });
  }

  unitControl = new FormControl<string | null>(null);
  filteredUnits$: Observable<OrganizationUnitItem[]> | null = null;
  filteredGroupedUnits$: Observable<{ levelName: string; units: OrganizationUnitItem[] }[]> | null = null;
  isLoading$: Observable<boolean>;
  private isSearching$ = new BehaviorSubject<boolean>(false);
  private destroy$ = new Subject<void>();
  private onTouched = () => {};
  private onChange = (_: UnitSelectorValue) => {};
  private isDisabled = false;
  isDefaultMode = false;

  /** Cache interno de unidades conocidas para resolver displayWith */
  _units: OrganizationUnitItem[] = [];

  get isFixedSource(): boolean {
    return Array.isArray(this.unitSource);
  }

  get showingSuggestions(): boolean {
    const value = this.unitControl.value;
    return !this.isFixedSource && this.suggestedUnits.length > 0 &&
      (!value || (typeof value === 'string' && !value.trim()));
  }

  constructor(
    private organizationUnitService: OrganizationUnitService,
    private cdr: ChangeDetectorRef
  ) {
    this.isLoading$ = this.isSearching$.asObservable();
  }

  ngOnInit(): void {
    this.unitControl.setValidators([this._validUnitValidator()]);
    this.unitControl.updateValueAndValidity();
    this._setupAutocomplete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['unitSource'] && !changes['unitSource'].firstChange) {
      // Cuando cambia la fuente (ej: se cargan las unidades manejables), reinicializar
      this._setupAutocomplete();
    }

    if (changes['loading']) {
      this.applyDisabledState();
    }

    if (changes['suggestedUnits'] || changes['levelId']) {
      this.onInputFocus();
    }

    if (changes['allowedLevelIds'] && !changes['allowedLevelIds'].firstChange) {
      this.clearDisallowedSelection();
    }

    if (this.unitControl.value && this._units.length > 0) {
      const currentValue = this.unitControl.value;
      this.unitControl.setValue(null, { emitEvent: false });
      this.unitControl.setValue(currentValue, { emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.isSearching$.complete();
  }

  // ─── ControlValueAccessor ──────────────────────────────────────────────────

  writeValue(value: UnitSelectorValue): void {
    if (this.multiple) {
      this.selectedUnitIds = [...new Map((Array.isArray(value) ? value : []).map(id => [id.toLowerCase(), id])).values()];
      this.unitControl.setValue(null, { emitEvent: false });
      for (const id of this.selectedUnitIds) {
        this.resolveSelectedUnit(id);
      }
      this.cdr.markForCheck();
      return;
    }
    const singleValue = typeof value === 'string' ? value : null;
    this.confirmedFreeText = this.allowFreeText && singleValue && !this.isGuid(singleValue) ? singleValue : null;
    this.isDefaultMode = this.allowDefaultUnit && !singleValue;
    this.unitControl.setValue(singleValue, { emitEvent: false });
    if (singleValue) {
      this._resolveUnitById(singleValue);
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: UnitSelectorValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.applyDisabledState();
  }

  // ─── Eventos del template ──────────────────────────────────────────────────

  onUnitSelected(unit: OrganizationUnitItem): void {
    if (this.multiple) {
      if (this.unitControl.disabled || !this.isAllowedLevel(unit) || this.selectedUnitIds.some(id => id.toLowerCase() === unit.id.toLowerCase())) return;
      this.selectedUnitCache.set(unit.id.toLowerCase(), unit);
      this.selectedUnitIds = [...this.selectedUnitIds, unit.id];
      this.unitControl.setValue('');
      this.onChange([...this.selectedUnitIds]);
      this.emitSelectedUnits();
      this.onTouched();
      this.cdr.markForCheck();
      return;
    }
    this.isDefaultMode = false;
    this.confirmedFreeText = null;
    this.unitControl.setValue(unit.id, { emitEvent: false });
    this.onChange(unit.id);
    this.unitSelected.emit(unit.id);
    this.unitChange.emit(unit);
    this.selectionChange.emit({ kind: 'unit', unit });
    this.onTouched();
    this.cdr.markForCheck();
  }

  onAutocompleteOptionSelected(unitId: string | FreeTextOption): void {
    if (typeof unitId !== 'string') {
      if (!this.allowFreeText || this.unitControl.disabled) return;
      const text = unitId.text.trim();
      if (!text) return;
      if (this.multiple) {
        if (this.selectedUnitIds.some(value => value.toLowerCase() === text.toLowerCase())) return;
        this.selectedUnitIds = [...this.selectedUnitIds, text];
        this.unitControl.setValue('');
        this.onChange([...this.selectedUnitIds]);
        this.emitSelectedUnits();
        this.onTouched();
        this.cdr.markForCheck();
        return;
      }
      this.confirmedFreeText = text;
      this.unitControl.setValue(text, { emitEvent: false });
      this.onChange(text);
      this.unitChange.emit(null);
      this.selectionChange.emit({ kind: 'text', text });
      this.onTouched();
      this.cdr.markForCheck();
      return;
    }
    const unit = this._units.find(u => u.id.toLowerCase() === unitId.toLowerCase());
    if (!unit) {
      return;
    }

    this.onUnitSelected(unit);
  }

  clearSelection(): void {
    if (this.multiple) {
      if (this.unitControl.disabled) return;
      this.selectedUnitIds = [];
      this.unitControl.setValue('');
      this.onChange([]);
      this.emitSelectedUnits();
      this.onTouched();
      this.cdr.markForCheck();
      return;
    }
    if (this.allowDefaultUnit) {
      this.useDefaultUnit();
      return;
    }

    this.confirmedFreeText = null;
    this.unitControl.setValue(null);
    this.onChange(null);
    this.unitSelected.emit('');
    this.unitChange.emit(null);
    this.selectionChange.emit(null);
  }

  changeUnit(): void {
    if (this.unitControl.disabled) return;
    this.isDefaultMode = false;
    this.onTouched();
    this.cdr.markForCheck();
  }

  useDefaultUnit(): void {
    if (this.unitControl.disabled) return;
    this.isDefaultMode = true;
    this.unitControl.setValue(null, { emitEvent: false });
    this.onChange(null);
    this.unitSelected.emit('');
    this.unitChange.emit(null);
    this.onTouched();
    this.cdr.markForCheck();
  }

  onInputFocus(): void {
    const value = this.unitControl.value;
    if (!this.unitControl.disabled && (!value || (typeof value === 'string' && !value.trim()))) {
      this.unitControl.setValue('');
    }
  }

  onSearchInput(): void {
    if (this.multiple) return;
    if (this.isDefaultMode) {
      this.isDefaultMode = false;
      this.onTouched();
    }
    const value = this.unitControl.value;
    if (this.allowFreeText) {
      this.confirmedFreeText = null;
      this.onChange(null);
      const text = typeof value === 'string' ? value.trim() : '';
      this.selectionChange.emit(text ? { kind: 'query', text } : null);
      this.onTouched();
    }
  }

  getPlaceholder(): string {
    if (this.allowDefaultUnit && this.isDefaultMode) {
      return this.defaultUnitName;
    }

    if (this.placeholder !== undefined) {
      return this.placeholder;
    }

    return this.required ? '' : 'Todas las unidades';
  }

  displayUnitName = (unitId: string | FreeTextOption | null): string => {
    if (!unitId) return '';
    if (typeof unitId !== 'string') return unitId.text;
    const unit = this._units.find(u => u.id.toLowerCase() === unitId.toLowerCase());
    if ((!unit || this.isUnresolvedGuidName(unit)) && this.isGuid(unitId)) return '';
    return unit ? unit.name : unitId;
  };

  // ─── Setup del autocomplete ────────────────────────────────────────────────

  private _setupAutocomplete(): void {
    if (this.isFixedSource) {
      this._setupLocalFilter();
    } else {
      this._setupBackendSearch();
    }
  }

  /** Filtra localmente cuando se provee una lista fija de unidades */
  private _setupLocalFilter(): void {
    const sourceItems = this.unitSource as OrganizationUnitItem[];
    this._units = sourceItems;

    this.filteredUnits$ = this.unitControl.valueChanges.pipe(
      startWith(this.unitControl.value ?? ''),
      map(value => {
        if (typeof value !== 'string') {
          return this.maxDisplayOptions ? sourceItems.slice(0, this.maxDisplayOptions) : sourceItems;
        }
        const term = this._normalize(value);
        const filtered = term.length === 0
          ? sourceItems
          : sourceItems.filter(u => this._normalize(u.name).includes(term));
        const available = this.availableUnits(filtered);
        return this.maxDisplayOptions ? available.slice(0, this.maxDisplayOptions) : available;
      }),
      takeUntil(this.destroy$)
    );

    if (this.groupByLevel) {
      this.filteredGroupedUnits$ = this.filteredUnits$.pipe(
        map(units => this._groupByLevel(units))
      );
    }
  }

  private _groupByLevel(units: OrganizationUnitItem[]): { levelName: string; units: OrganizationUnitItem[] }[] {
    const groups = new Map<string, OrganizationUnitItem[]>();
    for (const unit of units) {
      const key = this._getGroupKey(unit);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(unit);
    }
    return Array.from(groups.entries())
      .sort(([leftName, leftItems], [rightName, rightItems]) =>
        this._compareGroupEntries(leftName, leftItems, rightName, rightItems)
      )
      .map(([levelName, items]) => ({ levelName, units: items }));
  }

  private _getGroupKey(unit: OrganizationUnitItem): string {
    if (this.groupByMode === 'level') {
      return unit.levelName || 'Sin nivel';
    }

    if (this.groupByMode === 'ancestors') {
      return unit.ancestorNames && unit.ancestorNames.length > 0
        ? unit.ancestorNames.join(' › ')
        : (unit.levelName || 'Sin nivel');
    }

    return unit.ancestorNames && unit.ancestorNames.length > 0
      ? unit.ancestorNames.join(' › ')
      : (unit.levelName || 'Sin nivel');
  }

  private _compareGroupEntries(
    leftName: string,
    leftItems: OrganizationUnitItem[],
    rightName: string,
    rightItems: OrganizationUnitItem[]
  ): number {
    if (this.groupByMode !== 'level') {
      return leftName.localeCompare(rightName, 'es');
    }

    const leftOrder = this._getLevelGroupOrder(leftItems[0], leftName);
    const rightOrder = this._getLevelGroupOrder(rightItems[0], rightName);

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return leftName.localeCompare(rightName, 'es');
  }

  private _getLevelGroupOrder(unit: OrganizationUnitItem | undefined, levelName: string): number {
    const orderById = unit?.levelId ? LEVEL_GROUP_ORDER_BY_ID.get(unit.levelId) : undefined;
    if (orderById) return orderById;

    const normalizedName = this._normalize(levelName);
    if (normalizedName.includes('area')) return 1;
    if (normalizedName.includes('distrito')) return 2;
    if (normalizedName.includes('grupo')) return 3;
    if (normalizedName.includes('comunidad')) return 4;
    return Number.MAX_SAFE_INTEGER;
  }

  /** Búsqueda paginada en el backend cuando la fuente es `'all'` */
  private _setupBackendSearch(): void {
    this.filteredUnits$ = this.unitControl.valueChanges.pipe(
      startWith(this.unitControl.value || ''),
      distinctUntilChanged((previous, current) => previous === current && typeof current === 'string' && !!current.trim()),
      debounce(value => typeof value === 'string' && value.trim() ? timer(300) : of(0)),
      switchMap((value: any) => {
        if (typeof value !== 'string') {
          this.isSearching$.next(false);
          return of([]);
        }

        const searchTerm = value.trim();

        if (searchTerm.length === 0) {
          this.isSearching$.next(false);
          const suggestions = [...new Map(this.suggestedUnits
            .filter(unit => this.isAllowedLevel(unit) && (!this.levelId || unit.levelId === this.levelId))
            .map(unit => [unit.id.toLowerCase(), unit])).values()];
          this._units = [...new Map([...this._units, ...suggestions].map(unit => [unit.id.toLowerCase(), unit])).values()];
          return of(this.availableUnits(suggestions).slice(0, this.pageSize));
        }

        if (searchTerm.length < this.minSearchLength) {
          this.isSearching$.next(false);
          return of([]);
        }

        this.isSearching$.next(true);

        const effectiveLevelId = this.getSingleAllowedLevelId();

        return this.organizationUnitService.search(
          searchTerm,
          1,
          this.pageSize,
          effectiveLevelId
        ).pipe(
          tap(() => this.isSearching$.next(false)),
          catchError((error: any) => {
            console.error('Error searching organization units:', error);
            this.isSearching$.next(false);
            return of({ items: [], totalCount: 0, totalPages: 0 });
          }),
          map(response => {
            const items = response.items
              .map(dto => this._dtoToItem(dto))
              .filter(unit => this.isAllowedLevel(unit));
            this._units = items;
            this.unitControl.updateValueAndValidity({ emitEvent: false });
            this.cdr.markForCheck();
            return this.availableUnits(items);
          })
        );
      }),
      shareReplay(1),
      takeUntil(this.destroy$)
    );

    if (this.groupByLevel) {
      this.filteredGroupedUnits$ = this.filteredUnits$.pipe(
        map(units => this._groupByLevel(units))
      );
    }
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  /**
   * Normaliza un texto para búsqueda: minúsculas, sin acentos ni diacríticos.
   * Permite que "Plata" encuentre "Plátanos".
   */
  private _normalize(text: string): string {
    return text.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  /**
   * Cuando hay un valor inicial (ej: edición), resuelve el nombre de la unidad.
   * En modo fuente fija ya está en `_units`; en modo backend hace una llamada.
   */
  private _resolveUnitById(unitId: string): void {
    if (!this.isGuid(unitId)) return;

    if (this._units.some(u => u.id.toLowerCase() === unitId.toLowerCase())) {
      this.cdr.markForCheck();
      return;
    }

    if (this.isFixedSource) return;

    this.isSearching$.next(true);
    this.organizationUnitService.getById(unitId)
      .pipe(
        tap(() => this.isSearching$.next(false)),
        catchError((error: any) => {
          console.error('Error loading initial unit:', error);
          this.isSearching$.next(false);
          return of(null);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(dto => {
        if (dto) {
          this._units = [this._dtoToItem(dto)];
          if (this.clearDisallowedSelection()) {
            this.cdr.markForCheck();
            return;
          }

          const currentValue = this.unitControl.value;
          this.unitControl.setValue(null, { emitEvent: false });
          this.unitControl.setValue(currentValue, { emitEvent: false });
          this.unitControl.updateValueAndValidity({ emitEvent: false });
          this.cdr.markForCheck();
        }
      });
  }

  private _dtoToItem(dto: OrganizationUnitDto): OrganizationUnitItem {
    return {
      id: dto.id,
      name: dto.name,
      levelId: dto.levelId ?? dto.organizationLevelId ?? 0,
      levelName: dto.levelName ?? 'Sin nivel',
      parentId: dto.parentUnitId,
      ancestorNames: dto.ancestorNames ?? []
    };
  }

  private getSingleAllowedLevelId(): number | undefined {
    if (this.levelId) {
      return this.levelId;
    }

    return this.allowedLevelIds.length === 1 ? this.allowedLevelIds[0] : undefined;
  }

  private isAllowedLevel(unit: OrganizationUnitItem): boolean {
    return this.allowedLevelIds.length === 0 || this.allowedLevelIds.includes(unit.levelId);
  }

  private clearDisallowedSelection(): boolean {
    if (this.multiple) {
      const retained = this.selectedUnitIds.filter(id => {
        const unit = this.selectedUnitCache.get(id.toLowerCase());
        return !unit || this.isAllowedLevel(unit);
      });
      if (retained.length === this.selectedUnitIds.length) return false;
      this.selectedUnitIds = retained;
      this.onChange([...retained]);
      this.emitSelectedUnits();
      this.unitControl.setValue('');
      return true;
    }
    const unitId = this.unitControl.value;
    if (!unitId || this.allowedLevelIds.length === 0) {
      return false;
    }

    const unit = this._units.find(candidate => candidate.id === unitId);
    if (!unit?.levelId || this.isAllowedLevel(unit)) {
      return false;
    }

    this.clearSelection();
    return true;
  }

  private applyDisabledState(): void {
    if (this.isDisabled || this.loading) {
      this.unitControl.disable({ emitEvent: false });
    } else {
      this.unitControl.enable({ emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  private isGuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  }

  private isUnresolvedGuidName(unit: OrganizationUnitItem): boolean {
    return this.isGuid(unit.name) || unit.name === unit.id;
  }

  private _validUnitValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.multiple) return null;
      const value = control.value;
      if (!value) return null;
      if (this.allowFreeText && !this.isGuid(value)) return null;
      const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      return guidPattern.test(value) ? null : { invalidUnit: { value } };
    };
  }

  removeUnit(unitId: string): void {
    if (this.unitControl.disabled) return;
    this.selectedUnitIds = this.selectedUnitIds.filter(id => id.toLowerCase() !== unitId.toLowerCase());
    this.onChange([...this.selectedUnitIds]);
    this.emitSelectedUnits();
    this.unitControl.setValue('');
    this.onTouched();
    this.cdr.markForCheck();
  }

  private availableUnits(units: OrganizationUnitItem[]): OrganizationUnitItem[] {
    if (!this.multiple) return units;
    const selected = new Set(this.selectedUnitIds.map(id => id.toLowerCase()));
    return units.filter(unit => this.isAllowedLevel(unit) && !selected.has(unit.id.toLowerCase()));
  }

  private emitSelectedUnits(): void {
    const unitIds = this.selectedUnitIds.filter(id => !this.allowFreeText || this.isGuid(id));
    if (unitIds.every(id => this.selectedUnitCache.has(id.toLowerCase()))) {
      this.unitsChange.emit(unitIds.map(id => this.selectedUnitCache.get(id.toLowerCase())!));
    }
  }

  private resolveSelectedUnit(id: string): void {
    const source = Array.isArray(this.unitSource) ? this.unitSource : this._units;
    const cached = source.find(unit => unit.id.toLowerCase() === id.toLowerCase())
      ?? this.selectedUnitCache.get(id.toLowerCase());
    if (cached) {
      this.selectedUnitCache.set(id.toLowerCase(), cached);
      return;
    }
    if (this.isFixedSource || !this.isGuid(id)) return;
    this.organizationUnitService.getById(id).pipe(
      catchError(() => of(null)),
      takeUntil(this.destroy$),
    ).subscribe(dto => {
      if (!dto || !this.selectedUnitIds.some(selected => selected.toLowerCase() === id.toLowerCase())) return;
      this.selectedUnitCache.set(id.toLowerCase(), this._dtoToItem(dto));
      if (!this.clearDisallowedSelection()) this.emitSelectedUnits();
      this.cdr.markForCheck();
    });
  }
}
